<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import TlDialog from '@/components/TlDialog.vue';

// Scoring system info popup: what the system is (description), how results are entered,
// and the outcome/points table. Coloured dots encode win/loss/draw; a hollow warning dot
// marks outcomes that don't count towards bonus points.
const props = defineProps({
  scoringSystem: {
    type: Object,
    required: true
  },
  allowSplitGames: {
    type: Boolean,
    required: true
  },
  // this competition's customised points (wire shape: scoringSystemOutcomeId,
  // homePoints, awayPoints, homeBonus, awayBonus) — shown in place of the defaults
  overrides: {
    type: Array,
    default: () => []
  }
});
const dialog = ref(false);

const liveScoringSystem = computed(() => {
  return props.scoringSystem.outcomes ? props.scoringSystem : (item.value ?? props.scoringSystem);
});

const { getItem, itemPending, item } = useCrudApi('api/v2/scoringSystems');
onMounted(() => {
  checkForUpdates();
});

watch(() => props.scoringSystem, () => {
  checkForUpdates();
});

const checkForUpdates = () => {
  if(!props.scoringSystem.outcomes){
    getItem(props.scoringSystem.id);
  } else {
    item.value = null;
  }
};

const outcomeKind = (points, otherPoints) =>
    points > otherPoints ? 'win' : points < otherPoints ? 'loss' : 'draw';

// the competition's customised points replace the system defaults in the table
const effectiveOutcomes = computed(() => {
  const outcomes = liveScoringSystem.value.outcomes ?? [];
  const byOutcome = new Map(props.overrides.map((o) => [o.scoringSystemOutcomeId, o]));
  return outcomes.map((outcome) => {
    const override = byOutcome.get(outcome.id);
    return {
      ...outcome,
      homePoints: override?.homePoints ?? outcome.homePoints,
      awayPoints: override?.awayPoints ?? outcome.awayPoints,
      homeBonus: override?.homeBonus ?? outcome.homeBonus,
      awayBonus: override?.awayBonus ?? outcome.awayBonus,
      customised: !!override,
    };
  });
});

const hasCustomised = computed(() => effectiveOutcomes.value.some((o) => o.customised));
</script>

<template>
  <span>
    <v-btn class="ml-2" color="primary" variant="tonal" size="small" @click="dialog = true">
      Info
      <v-icon icon="mdi-information-outline" class="ml-1"></v-icon>
    </v-btn>

    <tl-dialog v-model="dialog" :title="liveScoringSystem.name" :max-width="560">
      <!-- what this system is -->
      <p v-if="liveScoringSystem.description" class="text-body-2 mb-3">
        {{ liveScoringSystem.description }}
      </p>

      <p class="text-body-2 text-medium-emphasis mb-4">
        {{
          allowSplitGames
              ? 'Results can be entered as individual game scores or as an overall result.'
              : 'Results are entered as an overall match result.'
        }}
      </p>

      <!-- key -->
      <div class="ssp-key mb-4">
        <span class="ssp-key__item"><span class="ssp-dot ssp-dot--win" />Win</span>
        <span class="ssp-key__item"><span class="ssp-dot ssp-dot--loss" />Loss</span>
        <span class="ssp-key__item"><span class="ssp-dot ssp-dot--draw" />Draw</span>
        <span class="ssp-key__item">
          <span class="ssp-dot ssp-dot--nobonus" />Doesn't count towards bonus points
        </span>
      </div>

      <!-- this competition awards its own points for the marked rows -->
      <v-alert v-if="hasCustomised" type="info" variant="tonal" density="compact" class="mb-3">
        Points have been customised for this competition. Marked rows show the
        customised values, not the system's defaults.
      </v-alert>

      <!-- outcomes -->
      <table v-if="liveScoringSystem.outcomes" class="ssp-table">
        <thead>
          <tr>
            <th>Side 1</th>
            <th>Side 2</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="outcome in effectiveOutcomes" :key="outcome.id" :class="{'ssp-row--custom': outcome.customised}">
            <td>
              <div class="ssp-cell">
                <span class="ssp-dot" :class="`ssp-dot--${outcomeKind(outcome.homePoints, outcome.awayPoints)}`" />
                <span v-if="!outcome.homeBonus" class="ssp-dot ssp-dot--nobonus" />
                <strong class="ssp-result">{{ outcome.homeResult }}</strong>
                <v-chip color="primary" size="x-small" :variant="outcome.customised ? 'flat' : 'tonal'">{{ outcome.homePoints }} pts</v-chip>
              </div>
            </td>
            <td>
              <div class="ssp-cell">
                <span class="ssp-dot" :class="`ssp-dot--${outcomeKind(outcome.awayPoints, outcome.homePoints)}`" />
                <span v-if="!outcome.awayBonus" class="ssp-dot ssp-dot--nobonus" />
                <strong class="ssp-result">{{ outcome.awayResult }}</strong>
                <v-chip color="primary" size="x-small" :variant="outcome.customised ? 'flat' : 'tonal'">{{ outcome.awayPoints }} pts</v-chip>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-else-if="itemPending" class="text-center py-4">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>
    </tl-dialog>
  </span>
</template>

<style scoped lang="scss">
/* coloured status dots (win / loss / draw); hollow warning dot = no bonus credit */
.ssp-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;

  &--win { background: rgb(var(--v-theme-success)); }
  &--loss { background: rgb(var(--v-theme-error)); }
  &--draw { background: rgb(var(--v-theme-border)); }
  &--nobonus {
    background: transparent;
    border: 2px solid rgb(var(--v-theme-warning));
  }
}

.ssp-key {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  padding: 10px 12px;
  background: rgb(var(--v-theme-light));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 10px;
}

.ssp-key__item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  font-weight: 600;
  color: rgb(var(--v-theme-charcoal));
}

.ssp-table {
  width: 100%;
  border-collapse: collapse;

  th {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: rgb(var(--v-theme-textSubtle));
    text-align: left;
    padding: 6px 10px;
    border-bottom: 1px solid rgb(var(--v-theme-borderLight));
  }

  td {
    padding: 7px 10px;
    border-bottom: 1px solid rgb(var(--v-theme-borderLight));
    width: 50%;
  }
}

.ssp-cell {
  display: flex;
  align-items: center;
  gap: 7px;
}

/* customised rows carry the same red accent strip as the customise-points dialog */
.ssp-row--custom td:first-child {
  box-shadow: inset 3px 0 0 rgb(var(--v-theme-primary));
}

.ssp-result {
  flex: 1;
  font-size: 13px;
  min-width: 0;
}
</style>
